import { useState } from 'react'
import { Newspaper, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react'
import type { MonitorNewsItem } from '@/lib/api'

interface MonitorNewsPanelProps {
  items: MonitorNewsItem[]
  loading?: boolean
  onRefresh?: () => void
}

function getTimeAgo(date: Date): string {
  const s = Math.floor((Date.now() - date.getTime()) / 1000)
  if (s < 60) return 'now'
  if (s < 3600) return `${Math.floor(s / 60)}m`
  if (s < 86400) return `${Math.floor(s / 3600)}h`
  return `${Math.floor(s / 86400)}d`
}

export default function MonitorNewsPanel({ items, loading, onRefresh }: MonitorNewsPanelProps) {
  const [expandedSource, setExpandedSource] = useState<string | null>(null)

  const bySource = items.reduce<Record<string, MonitorNewsItem[]>>((acc, item) => {
    const src = item.source || 'Other'
    if (!acc[src]) acc[src] = []
    acc[src].push(item)
    return acc
  }, {})
  const sources = Object.keys(bySource).sort()

  return (
    <div className="bg-gray-950/95 backdrop-blur border border-green-900/50 rounded overflow-hidden shadow-[0_0_15px_rgba(34,197,94,0.06)]">
      <div className="flex items-center justify-between px-3 py-2 border-b border-green-900/40">
        <span className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.15em] text-green-400">
          <Newspaper className="w-3.5 h-3.5" />
          News
        </span>
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={loading}
            className="text-[9px] font-mono text-gray-500 hover:text-green-400 transition-colors disabled:opacity-50"
          >
            {loading ? '…' : 'Refresh'}
          </button>
        )}
      </div>
      <div className="max-h-[320px] overflow-y-auto">
        {loading && items.length === 0 ? (
          <div className="px-3 py-4 text-[10px] font-mono text-gray-600">Loading sources…</div>
        ) : sources.length === 0 ? (
          <div className="px-3 py-4 text-[10px] font-mono text-gray-600">No articles loaded</div>
        ) : (
          <ul className="divide-y divide-gray-800/30">
            {sources.map((source) => {
              const articles = bySource[source]
              const isExpanded = expandedSource === source
              return (
                <li key={source}>
                  <button
                    onClick={() => setExpandedSource(isExpanded ? null : source)}
                    className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-green-500/10 transition-colors"
                  >
                    <span className="text-[10px] font-mono text-gray-300 truncate max-w-[180px]" title={source}>
                      {source}
                    </span>
                    <span className="flex items-center gap-1 text-[9px] text-gray-600 font-mono">
                      {articles.length}
                      {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </span>
                  </button>
                  {isExpanded && (
                    <ul className="bg-gray-950/50 border-t border-gray-800/30 pb-2">
                      {articles.slice(0, 8).map((art, i) => (
                        <li key={`${art.link}-${i}`}>
                          <a
                            href={art.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-start gap-1.5 px-3 py-1.5 hover:bg-green-500/10 transition-colors group"
                          >
                            <ExternalLink className="w-3 h-3 text-gray-600 group-hover:text-green-400 shrink-0 mt-0.5" />
                            <div className="min-w-0">
                              <p className="text-[10px] text-gray-400 leading-snug line-clamp-2 group-hover:text-gray-300">
                                {art.title}
                              </p>
                              <p className="text-[8px] text-gray-600 font-mono mt-0.5">
                                {getTimeAgo(new Date(art.published))}
                              </p>
                            </div>
                          </a>
                        </li>
                      ))}
                      {articles.length > 8 && (
                        <li className="px-3 py-1 text-[8px] text-gray-600 font-mono">
                          +{articles.length - 8} more
                        </li>
                      )}
                    </ul>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
